/**
 * Anchor-watch state: where the anchor is, the rode/depth inputs that size the
 * swing circle, the scope ratio we're planning to, a GPS-wander buffer, and a
 * rolling swing track of where the boat has actually sat (so you can see the
 * swing pattern and spot a drag). The live drag alarm is computed from this
 * against the boat position, the same way the AIS alarms are computed from the
 * target store.
 *
 * The config here is persisted: Android reaps a backgrounded app hard, and a
 * skipper who dropped the hook and set the scope would otherwise lose the whole
 * watch (anchor position included) the moment the app is killed overnight, which
 * is exactly when an anchor watch matters most. The swing track is deliberately
 * NOT persisted — it's a rolling live trail, not a setting.
 */
import { create } from 'zustand';
import { loadJson, saveJson } from './persist';
import { circleToZone, translateZone } from '@/safety/anchorZone';

const MAX_SWING_POINTS = 600;

/** How the rode is made up, which sets the sensible default scope ratio. */
export type RodeType = 'chain' | 'rope';

/** The alarm boundary: the simple swing circle, or a custom-drawn zone polygon. */
export type AnchorZoneMode = 'radius' | 'zone';

interface AnchorWatchState {
  /** Anchor position [lon, lat], or null when not anchored. */
  anchor: [number, number] | null;
  scopeM: number;
  depthM: number;
  bowHeightM: number;
  /** Desired scope ratio (rode : vertical drop). 4:1 on chain, 6:1 on rope. */
  scopeRatio: number;
  /** Chain or rope, so we can label the scope and default the ratio sensibly. */
  rodeType: RodeType;
  /** GPS-wander buffer added to the swing radius before the alarm trips, metres. */
  bufferM: number;
  /**
   * Which boundary the drag alarm watches: the quick swing circle ('radius', the
   * default), or a custom polygon the skipper drew on the chart ('zone', the
   * advanced option, e.g. to hug a channel edge or keep off a lee shore).
   */
  mode: AnchorZoneMode;
  /** The custom swing/no-drag polygon [lon,lat], used when mode === 'zone'. */
  zone: [number, number][];
  /** True while the skipper is tapping the chart to lay down the zone polygon. */
  drawingZone: boolean;
  /** Recent boat positions, for the swing-track overlay. */
  swingTrack: [number, number][];

  dropAnchor: (pos: [number, number]) => void;
  weighAnchor: () => void;
  setScopeM: (m: number) => void;
  setDepthM: (m: number) => void;
  setBowHeightM: (m: number) => void;
  setScopeRatio: (r: number) => void;
  setRodeType: (t: RodeType) => void;
  setBufferM: (m: number) => void;
  setMode: (m: AnchorZoneMode) => void;
  setDrawingZone: (on: boolean) => void;
  addZonePoint: (pos: [number, number]) => void;
  undoZonePoint: () => void;
  clearZone: () => void;
  /**
   * Turn the swing CIRCLE into a ring of draggable points, so the boundary is
   * edited rather than drawn from scratch. Starting from the circle matters:
   * the circle is already the right answer nearly everywhere, and the skipper
   * only wants to pull it in on the side with the rocks.
   */
  seedZoneFromCircle: (centre: [number, number], radiusNm: number, points?: number) => void;
  /** Drag one boundary point in or out. */
  moveZonePoint: (index: number, pos: [number, number]) => void;
  /** Drop a point. Refuses below three, which is the least that is still an area. */
  removeZonePoint: (index: number) => void;
  /** Insert a point AFTER `index`, the long-press-between-two-points action. */
  insertZonePoint: (index: number, pos: [number, number]) => void;
  pushSwing: (pos: [number, number]) => void;
}

/** The default scope ratio for a rode type: 4:1 on chain, 6:1 on warp. */
export function defaultScopeRatio(t: RodeType): number {
  return t === 'chain' ? 4 : 6;
}

/** The slice of anchor-watch state we persist (everything but the live track). */
interface AnchorWatchPersist {
  anchor: [number, number] | null;
  scopeM: number;
  depthM: number;
  bowHeightM: number;
  scopeRatio: number;
  rodeType: RodeType;
  bufferM: number;
  mode: AnchorZoneMode;
  zone: [number, number][];
}

const ANCHOR_LS = 'acp.anchorWatch.v1';

const DEFAULTS: AnchorWatchPersist = {
  anchor: null,
  scopeM: 30,
  depthM: 5,
  bowHeightM: 1,
  scopeRatio: 4,
  rodeType: 'chain',
  bufferM: 15,
  mode: 'radius',
  zone: [],
};

function persist(s: AnchorWatchState): void {
  const snap: AnchorWatchPersist = {
    anchor: s.anchor,
    scopeM: s.scopeM,
    depthM: s.depthM,
    bowHeightM: s.bowHeightM,
    scopeRatio: s.scopeRatio,
    rodeType: s.rodeType,
    bufferM: s.bufferM,
    mode: s.mode,
    zone: s.zone,
  };
  saveJson(ANCHOR_LS, snap);
}

export const useAnchorWatchStore = create<AnchorWatchState>((set) => {
  const saved = loadJson<AnchorWatchPersist>(ANCHOR_LS, DEFAULTS);
  return {
    anchor: saved.anchor,
    scopeM: saved.scopeM,
    depthM: saved.depthM,
    bowHeightM: saved.bowHeightM,
    scopeRatio: saved.scopeRatio,
    rodeType: saved.rodeType,
    bufferM: saved.bufferM,
    mode: saved.mode,
    zone: saved.zone,
    drawingZone: false,
    swingTrack: [],
    dropAnchor: (anchor) =>
      set((s) => {
        // The shaped boundary is drawn around the anchor and means nothing
        // without it, so it travels with it. Leaving it behind put the ring on
        // the chart over the old spot while the hook symbol moved to the new
        // one, which reads as the anchor not having landed where it was placed,
        // and left the alarm watching water the boat was no longer in
        // (fb-msdpfn95-0). Re-placing the anchor is nearly always correcting
        // it, so the side we pulled in stays on the same side of the anchor.
        const zone = s.anchor ? translateZone(s.zone, s.anchor, anchor) : s.zone;
        const next = { ...s, anchor, zone, swingTrack: [] };
        persist(next);
        return { anchor, zone, swingTrack: [] };
      }),
    weighAnchor: () =>
      set((s) => {
        const next = { ...s, anchor: null, swingTrack: [] };
        persist(next);
        return { anchor: null, swingTrack: [], drawingZone: false };
      }),
    setScopeM: (scopeM) =>
      set((s) => {
        persist({ ...s, scopeM });
        return { scopeM };
      }),
    setDepthM: (depthM) =>
      set((s) => {
        persist({ ...s, depthM });
        return { depthM };
      }),
    setBowHeightM: (bowHeightM) =>
      set((s) => {
        persist({ ...s, bowHeightM });
        return { bowHeightM };
      }),
    setScopeRatio: (scopeRatio) =>
      set((s) => {
        persist({ ...s, scopeRatio });
        return { scopeRatio };
      }),
    setRodeType: (rodeType) =>
      set((s) => {
        persist({ ...s, rodeType });
        return { rodeType };
      }),
    setBufferM: (bufferM) =>
      set((s) => {
        persist({ ...s, bufferM });
        return { bufferM };
      }),
    setMode: (mode) =>
      set((s) => {
        persist({ ...s, mode });
        // Leaving zone mode also stops any in-progress drawing.
        return { mode, drawingZone: mode === 'zone' ? s.drawingZone : false };
      }),
    setDrawingZone: (drawingZone) => set({ drawingZone }),
    addZonePoint: (pos) =>
      set((s) => {
        const zone = [...s.zone, pos];
        persist({ ...s, zone });
        return { zone };
      }),
    undoZonePoint: () =>
      set((s) => {
        if (!s.zone.length) return s;
        const zone = s.zone.slice(0, -1);
        persist({ ...s, zone });
        return { zone };
      }),
    clearZone: () =>
      set((s) => {
        persist({ ...s, zone: [] });
        return { zone: [] };
      }),
    seedZoneFromCircle: (centre, radiusNm, points) =>
      set((s) => {
        const zone = circleToZone(centre, radiusNm, points);
        persist({ ...s, zone, mode: 'zone' });
        return { zone, mode: 'zone' as AnchorZoneMode, drawingZone: false };
      }),
    moveZonePoint: (index, pos) =>
      set((s) => {
        if (index < 0 || index >= s.zone.length) return s;
        const zone = s.zone.map((p, i) => (i === index ? pos : p));
        persist({ ...s, zone });
        return { zone };
      }),
    removeZonePoint: (index) =>
      set((s) => {
        // Three points is the least that still encloses water. Below that the
        // alarm has no area to test against, and silently emptying the boundary
        // on an anchor watch is not a thing to do at two in the morning.
        if (index < 0 || index >= s.zone.length || s.zone.length <= 3) return s;
        const zone = s.zone.filter((_, i) => i !== index);
        persist({ ...s, zone });
        return { zone };
      }),
    insertZonePoint: (index, pos) =>
      set((s) => {
        if (index < 0 || index >= s.zone.length) return s;
        const zone = [...s.zone];
        zone.splice(index + 1, 0, pos);
        persist({ ...s, zone });
        return { zone };
      }),
    pushSwing: (pos) =>
      set((s) => {
        if (!s.anchor) return s;
        const next = [...s.swingTrack, pos];
        if (next.length > MAX_SWING_POINTS) next.splice(0, next.length - MAX_SWING_POINTS);
        return { swingTrack: next };
      }),
  };
});
